import React, { useState, useEffect, useMemo } from "react";
import {
  Search,
  X,
  ChevronLeft,
  Building2,
  Check,
  Plus,
  Edit3,
  Globe,
  MapPin,
  ChevronDown,
} from "lucide-react";
import { db } from "../firebase";
import {
  collection,
  onSnapshot,
  query,
  orderBy,
  doc,
  deleteDoc,
  addDoc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import ilceKoyler from "../json/ilce-koyler.json";
import ilIlceData from "../json/il-ilceler.json";
import ulkeSehirData from "../json/ulke-sehir.json";
import ProductsSection, {
  uploadAndPrepareProducts,
} from "../components/ProductsSection";

const COUNTRIES = (() => {
  const uniqueCountries = [...new Set(ulkeSehirData.map((item) => item.ulke))];
  const others = uniqueCountries
    .filter((c) => c !== "Türkiye")
    .sort((a, b) => a.localeCompare(b, "tr"));
  return ["Türkiye", ...others];
})();

const PRIORITY_CITIES = ["İstanbul", "Sivas", "Ankara", "İzmir"];

const TURKEY_CITIES = (() => {
  const allCities = ilIlceData.map((il) => il.text);

  const priority = allCities
    .filter((city) =>
      PRIORITY_CITIES.some(
        (p) => p.toLocaleLowerCase("tr") === city.toLocaleLowerCase("tr")
      )
    )
    .sort((a, b) => {
      const aIndex = PRIORITY_CITIES.findIndex(
        (p) => p.toLocaleLowerCase("tr") === a.toLocaleLowerCase("tr")
      );
      const bIndex = PRIORITY_CITIES.findIndex(
        (p) => p.toLocaleLowerCase("tr") === b.toLocaleLowerCase("tr")
      );
      return aIndex - bIndex;
    });

  const others = allCities
    .filter((city) => !priority.includes(city))
    .sort((a, b) => a.localeCompare(b, "tr"));

  return [...priority, ...others];
})();
const toTitleTR = (s = "") =>
  s
    .toLocaleLowerCase("tr")
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .map((w) => (w ? w[0].toLocaleUpperCase("tr") + w.slice(1) : ""))
    .join(" ");

const normTR = (s = "") =>
  s
    .toString()
    .normalize("NFKD")
    .replace(/\p{Diacritic}/gu, "")
    .toLocaleLowerCase("tr")
    .replace(/\s+/g, " ")
    .trim();

function SectionHeading({ children }) {
  return (
    <div className="flex items-center gap-2 pt-3 pb-1">
      <span className="text-xs font-semibold text-slate-400 uppercase tracking-widest">
        {children}
      </span>
      <div className="flex-1 h-px bg-slate-100" />
    </div>
  );
}

function InfoRow({ label, value }) {
  if (!value) return null;
  return (
    <div className="py-2 border-b border-slate-100 last:border-0">
      <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-0.5">
        {label}
      </p>
      <p className="text-sm text-slate-800 break-words">{String(value)}</p>
    </div>
  );
}

function AInput({ value, onChange, placeholder, type = "text" }) {
  return (
    <input
      type={type}
      value={value || ""}
      onChange={onChange}
      placeholder={placeholder}
      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800
        focus:outline-none focus:ring-2 focus:ring-slate-900 placeholder:text-slate-400 transition"
    />
  );
}

function ATextarea({ value, onChange, placeholder, rows = 3 }) {
  return (
    <textarea
      value={value || ""}
      onChange={onChange}
      placeholder={placeholder}
      rows={rows}
      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800
        focus:outline-none focus:ring-2 focus:ring-slate-900 placeholder:text-slate-400 transition resize-none"
    />
  );
}

function ASelect({ value, onChange, children, disabled }) {
  return (
    <select
      value={value || ""}
      onChange={onChange}
      disabled={disabled}
      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800
        focus:outline-none focus:ring-2 focus:ring-slate-900 transition appearance-none bg-white
        disabled:bg-slate-50 disabled:text-slate-400 disabled:cursor-not-allowed"
    >
      {children}
    </select>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-500 mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}

export default function PendingFirmsTab({ showStatus }) {
  const [firms, setFirms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null); // view/edit data
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editProducts, setEditProducts] = useState([]);
  const [country, setCountry] = React.useState("Türkiye");
  const [city, setCity] = React.useState("");
  const [cityDistrict, setCityDistrict] = React.useState("");

  // Türkiye seçili mi?
  const isTurkiye = country === "Türkiye";

  // Seçilen ile göre ilçeleri al (il-ilceler.json'dan) - SADECE TÜRKİYE İÇİN
  const cityDistricts = React.useMemo(() => {
    if (!city || !isTurkiye) return [];
    const ilData = ilIlceData.find((il) => normTR(il.text) === normTR(city));
    if (!ilData) return [];
    return ilData.districts
      .map((d) => d.text)
      .sort((a, b) => a.localeCompare(b, "tr"));
  }, [city, isTurkiye]);

  const ilceler = useMemo(
    () =>
      [...new Set(ilceKoyler.map((x) => x.ilce))].sort((a, b) =>
        a.localeCompare(b, "tr")
      ),
    []
  );
  const koyler = useMemo(() => {
    const ilce = form?.sivasIlce || selected?.sivasIlce;
    if (!ilce) return [];
    const list = ilceKoyler.filter((x) => x.ilce === ilce).map((x) => x.koy);
    const merkez = list.filter((k) =>
      k.toLocaleLowerCase("tr").includes("merkez")
    );
    const diger = list
      .filter((k) => !k.toLocaleLowerCase("tr").includes("merkez"))
      .sort((a, b) => a.localeCompare(b, "tr"));
    return [...merkez, ...diger];
  }, [form?.sivasIlce, selected?.sivasIlce]);

  const isFirstRender = React.useRef(true);

  React.useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setCity("");
    setCityDistrict("");
  }, [country]);

  // İl değiştiğinde ilçeyi sıfırla
  React.useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setCityDistrict("");
  }, [city]);

  const setF = (patch) => setForm((p) => ({ ...p, ...patch }));

  useEffect(() => {
    const q = query(
      collection(db, "firmalarBekleyen"),
      orderBy("createdAt", "desc")
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setFirms(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      () => setLoading(false)
    );
    return () => unsub();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLocaleLowerCase("tr");
    if (!q) return firms;
    return firms.filter((f) =>
      `${f.firmaAdi || ""} ${f.sehir || ""} ${f.ilce || ""} ${(
        f.secilenSektorler || []
      ).join(" ")}`
        .toLocaleLowerCase("tr")
        .includes(q)
    );
  }, [firms, search]);

  const openDetail = (firm) => {
    setSelected(firm);
    setForm({ ...firm });
    setEditMode(false);
    setCountry(firm.ulke || "Türkiye");
    setCity(firm.sehir || "");
    setCityDistrict(firm.ilce || "");
    setEditProducts(
      (firm.urunler || []).map((p) => ({
        _key: Math.random().toString(36).slice(2),
        ad: p.ad || "",
        aciklama: p.aciklama || "",
        imageUrl: p.imageUrl || "",
        imageFile: null,
        imagePreviewUrl: "",
      }))
    );
  };

  const handleDeleteFromList = async (e, firm) => {
    e.stopPropagation();
    if (!window.confirm(`"${firm.firmaAdi}" silinsin mi?`)) return;
    try {
      await deleteDoc(doc(db, "firmalarBekleyen", firm.id));
      showStatus("success", "Kayıt silindi.");
    } catch (err) {
      showStatus("error", "Silinemedi: " + err.message);
    }
  };

  /* Sadece güncelle, onaylama */
  const handleSaveOnly = async () => {
    setBusy(true);
    try {
      const uploadedProducts = await uploadAndPrepareProducts(editProducts);
      const { id, ...data } = form;
      await updateDoc(doc(db, "firmalarBekleyen", selected.id), {
        ...data,
        urunler: uploadedProducts,
        updatedAt: serverTimestamp(),
      });
      setSelected({ ...form, id: selected.id, urunler: uploadedProducts });
      setEditMode(false);
      showStatus("success", "Değişiklikler kaydedildi.");
    } catch (err) {
      showStatus("error", "Hata: " + err.message);
    } finally {
      setBusy(false);
    }
  };

  /* Kaydet + Onayla */
  const handleSaveAndApprove = async () => {
    if (!window.confirm(`"${form.firmaAdi}" kaydedilip onaylanacak. Devam?`))
      return;
    setBusy(true);
    try {
      const uploadedProducts = await uploadAndPrepareProducts(editProducts);
      const { isBekleyen, owner, id, ...firmData } = form;
      await addDoc(collection(db, "firmalar"), {
        ...firmData,
        urunler: uploadedProducts,
        createdAt: selected.createdAt || serverTimestamp(),
        approvedAt: serverTimestamp(),
        source: "user_approved",
      });
      await deleteDoc(doc(db, "firmalarBekleyen", selected.id));
      showStatus("success", "Firma kaydedildi ve onaylandı.");
      setSelected(null);
      setForm(null);
      setEditMode(false);
    } catch (err) {
      showStatus("error", "Hata: " + err.message);
    } finally {
      setBusy(false);
    }
  };

  /* Düzenleme yapmadan onayla */
  const handleApprove = async () => {
    if (!window.confirm(`"${selected.firmaAdi}" onaylanacak. Devam?`)) return;
    setBusy(true);
    try {
      const { isBekleyen, owner, id, ...firmData } = selected;
      await addDoc(collection(db, "firmalar"), {
        ...firmData,
        createdAt: selected.createdAt || serverTimestamp(),
        approvedAt: serverTimestamp(),
        source: "user_approved",
      });
      await deleteDoc(doc(db, "firmalarBekleyen", selected.id));
      showStatus("success", "Firma onaylandı.");
      setSelected(null);
      setForm(null);
    } catch (err) {
      showStatus("error", "Hata: " + err.message);
    } finally {
      setBusy(false);
    }
  };

  /* Reddet */
  const handleReject = async () => {
    if (!window.confirm(`"${selected.firmaAdi}" reddedilsin mi?`)) return;
    setBusy(true);
    try {
      await deleteDoc(doc(db, "firmalarBekleyen", selected.id));
      showStatus("success", "Firma reddedildi.");
      setSelected(null);
      setForm(null);
      setEditMode(false);
    } catch (err) {
      showStatus("error", "Hata: " + err.message);
    } finally {
      setBusy(false);
    }
  };

  /* ═══ DETAY / DÜZENLEME ═══ */
  if (selected)
    return (
      <div>
        {/* Geri */}
        <button
          type="button"
          onClick={() => {
            setSelected(null);
            setEditMode(false);
          }}
          className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-5 transition"
        >
          <ChevronLeft className="w-4 h-4" /> Listeye Dön
        </button>

        {/* Başlık */}
        <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl border border-slate-100 mb-4">
          <div className="w-14 h-14 rounded-xl border border-slate-200 bg-white overflow-hidden shrink-0 flex items-center justify-center">
            {selected.logoUrl ? (
              <img
                src={selected.logoUrl}
                alt={selected.firmaAdi}
                className="w-full h-full object-contain p-1"
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            ) : (
              <Building2 className="w-5 h-5 text-slate-300" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-slate-900">
              {editMode ? form.firmaAdi : selected.firmaAdi}
            </h3>
            <p className="text-xs text-slate-400 mt-0.5">
              {[selected.sehir, selected.ilce].filter(Boolean).join(" / ")}
            </p>
          </div>
          <span className="text-[10px] px-2 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200 font-semibold shrink-0">
            Onay Bekliyor
          </span>
        </div>

        {/* ─── GÖRÜNTÜLEME MODU ─── */}
        {!editMode && (
          <>
            {/* Butonlar */}
            <div className="flex gap-2 mb-5">
              <button
                type="button"
                onClick={() => setEditMode(true)}
                className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm border border-slate-200 text-slate-700 hover:bg-slate-50 transition font-medium"
              >
                <Edit3 className="w-3.5 h-3.5" /> Düzenle
              </button>
            </div>

            {/* Detaylar */}
            <div className="bg-white rounded-xl border border-slate-100 p-4 space-y-0">
              <SectionHeading>Temel Bilgiler</SectionHeading>
              <InfoRow label="Firma Adı" value={selected.firmaAdi} />
              <InfoRow label="E-posta" value={selected.email} />
              <InfoRow label="Web" value={selected.web} />
              <InfoRow label="Sosyal Medya" value={selected.sosyalMedya} />
              <InfoRow label="Tanıtım Filmi" value={selected.tanitimVideo} />
              <InfoRow label="İşletme Türü" value={selected.isletmeTuru} />
              <InfoRow label="Kuruluş Yılı" value={selected.kurulusYili} />
              <InfoRow label="Yetkili Kişi" value={selected.yetkiliKisi} />
              <InfoRow
                label="Yetkili Pozisyon"
                value={selected.yetkiliPozisyon}
              />
              <InfoRow
                label="Çalışma Saatleri"
                value={selected.calismaSaatleri}
              />

              <SectionHeading>İletişim</SectionHeading>
              <InfoRow label="Telefon" value={selected.telefon} />
              <InfoRow label="Faks" value={selected.fax} />
              <InfoRow label="GSM" value={selected.gsm} />

              <SectionHeading>Konum</SectionHeading>
              <InfoRow label="Ülke" value={selected.ulke} />
              <InfoRow label="Şehir / İl" value={selected.sehir} />
              <InfoRow label="İlçe" value={selected.ilce} />
              <InfoRow label="Açık Adres" value={selected.acikAdres} />
              <InfoRow label="Sivas İlçe" value={selected.sivasIlce} />
              <InfoRow label="Köy / Mahalle" value={selected.sivasKoy} />
              

              <SectionHeading>Sektör & Kelimeler</SectionHeading>
              {selected.secilenSektorler?.length > 0 && (
                <div className="py-2 border-b border-slate-100">
                  <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">
                    Sektör
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {selected.secilenSektorler.map((s, i) => (
                      <span
                        key={i}
                        className="px-2.5 py-0.5 bg-amber-50 text-amber-800 border border-amber-200 rounded-full text-xs font-medium"
                      >
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {selected.altSektorler?.length > 0 && (
                <div className="py-2 border-b border-slate-100">
                  <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">
                    Alt Sektör
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {selected.altSektorler.map((a, i) => (
                      <span
                        key={i}
                        className="px-2.5 py-0.5 bg-amber-50 text-amber-800 border border-amber-200 rounded-full text-xs font-medium"
                      >
                        {a}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {selected.anahtarKelimeler?.length > 0 && (
                <div className="py-2">
                  <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">
                    Anahtar Kelimeler
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {selected.anahtarKelimeler.map((k, i) => (
                      <span
                        key={i}
                        className="px-2.5 py-0.5 bg-slate-100 text-slate-600 rounded-full text-xs"
                      >
                        {k}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              <SectionHeading>Ürünler</SectionHeading>
              {selected.urunler?.length > 0 && (
                <div>
                  <div className="text-sm font-medium text-gray-700 mb-2">
                    Ürünler & Hizmetler ({selected.urunler.length})
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {selected.urunler.map((p, i) => (
                      <div
                        key={i}
                        className="p-2 bg-gray-50 rounded-lg border flex items-center gap-2"
                      >
                        {(p.imagePreviewUrl || p.imageUrl) && (
                          <img
                            src={p.imagePreviewUrl || p.imageUrl}
                            alt={p.ad}
                            className="w-10 h-10 object-cover rounded shrink-0"
                            onError={(e) => {
                              e.target.style.display = "none";
                            }}
                          />
                        )}
                        <div className="min-w-0">
                          <p className="text-xs font-medium text-gray-700 line-clamp-1">
                            {p.ad || "İsimsiz"}
                          </p>
                          {p.aciklama && (
                            <p className="text-[10px] text-gray-400 line-clamp-1">
                              {p.aciklama}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selected.hakkinda && (
                <>
                  <SectionHeading>Firma Hakkında</SectionHeading>
                  <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-line pt-2">
                    {selected.hakkinda}
                  </p>
                </>
              )}
              {/* Butonlar */}
              <div className="flex gap-2 mb-5">
                <button
                  type="button"
                  onClick={handleApprove}
                  disabled={busy}
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white py-2.5 rounded-xl text-sm font-semibold transition disabled:opacity-50"
                >
                  <Check className="w-4 h-4" /> {busy ? "…" : "Onayla"}
                </button>
                <button
                  type="button"
                  onClick={handleReject}
                  disabled={busy}
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-red-500 hover:bg-red-600 text-white py-2.5 rounded-xl text-sm font-semibold transition disabled:opacity-50"
                >
                  <X className="w-4 h-4" /> {busy ? "…" : "Reddet"}
                </button>
              </div>
            </div>
          </>
        )}

        {/* ─── DÜZENLEME MODU ─── */}
        {editMode && form && (
          <>
            <div className="space-y-4">
              <SectionHeading>Temel Bilgiler</SectionHeading>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Firma Adı">
                  <AInput
                    value={form.firmaAdi}
                    onChange={(e) => setF({ firmaAdi: e.target.value })}
                  />
                </Field>
                <Field label="E-posta">
                  <AInput
                    value={form.email}
                    onChange={(e) => setF({ email: e.target.value })}
                    placeholder="ornek@email.com"
                  />
                </Field>
                <Field label="Web Sitesi">
                  <AInput
                    value={form.web}
                    onChange={(e) => setF({ web: e.target.value })}
                    placeholder="https://…"
                  />
                </Field>
                <Field label="Sosyal Medya">
                  <AInput
                    value={form.sosyalMedya}
                    onChange={(e) => setF({ sosyalMedya: e.target.value })}
                  />
                </Field>
                <Field label="Tanıtım Filmi">
                  <AInput
                    value={form.tanitimVideo}
                    onChange={(e) => setF({ tanitimVideo: e.target.value })}
                  />
                </Field>
                <Field label="İşletme Türü">
                  <AInput
                    value={form.isletmeTuru}
                    onChange={(e) => setF({ isletmeTuru: e.target.value })}
                  />
                </Field>
                <Field label="Kuruluş Yılı">
                  <AInput
                    value={form.kurulusYili}
                    onChange={(e) => setF({ kurulusYili: e.target.value })}
                  />
                </Field>
                <Field label="Yetkili Kişi">
                  <AInput
                    value={form.yetkiliKisi}
                    onChange={(e) => setF({ yetkiliKisi: e.target.value })}
                  />
                </Field>
                <Field label="Yetkili Pozisyon">
                  <AInput
                    value={form.yetkiliPozisyon}
                    onChange={(e) => setF({ yetkiliPozisyon: e.target.value })}
                  />
                </Field>
                <Field label="Çalışma Saatleri">
                  <AInput
                    value={form.calismaSaatleri}
                    onChange={(e) => setF({ calismaSaatleri: e.target.value })}
                  />
                </Field>
              </div>

              <SectionHeading>İletişim</SectionHeading>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Field label="Telefon">
                  <AInput
                    value={form.telefon}
                    onChange={(e) => setF({ telefon: e.target.value })}
                  />
                </Field>
                <Field label="Faks">
                  <AInput
                    value={form.fax}
                    onChange={(e) => setF({ fax: e.target.value })}
                  />
                </Field>
                <Field label="GSM">
                  <AInput
                    value={form.gsm}
                    onChange={(e) => setF({ gsm: e.target.value })}
                  />
                </Field>
              </div>

              <SectionHeading>Konum</SectionHeading>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Field label="Ülke">
                  {/* Ülke Dropdown */}
                  <div className="relative flex items-center bg-white rounded-xl shadow-lg h-14 md:h-16 px-4 md:px-5">
                    <Globe className="text-gray-400 w-5 h-5 mr-3 shrink-0" />
                    <div className="min-w-0 w-full relative">
                      <select
                        value={form.ulke}
                        onChange={(e) => {
                          setCountry(e.target.value);
                          setForm((p) => ({
                            ...p,
                            ulke: e.target.value,
                            sehir: "",
                            ilce: "",
                          }));
                        }}
                        className="min-w-0 w-full outline-none text-gray-800 text-[14px] md:text-[16px] bg-transparent appearance-none cursor-pointer"
                      >
                        {COUNTRIES.map((ulke) => (
                          <option key={ulke} value={ulke}>
                            {ulke}
                          </option>
                        ))}
                      </select>
                    </div>
                    <ChevronDown className="text-gray-400 w-4 h-4 shrink-0 pointer-events-none" />
                  </div>
                </Field>

                <Field label="Şehir / İl">
                  <div className="relative flex items-center bg-white rounded-xl shadow-lg h-14 md:h-16 px-4 md:px-5">
                    <MapPin className="text-gray-400 w-5 h-5 mr-3 shrink-0" />
                    <div className="min-w-0 w-full relative">
                      {!city && (
                        <span className="absolute inset-0 flex items-center text-gray-400 text-[12px] md:text-[14px] pointer-events-none">
                          {isTurkiye ? "İl seçiniz..." : "Sadece Türkiye"}
                        </span>
                      )}
                      <select
                        value={form.sehir}
                        onChange={(e) => {
                          setCity(e.target.value);
                          setForm((p) => ({
                            ...p,
                            sehir: e.target.value,
                            ilce: "",
                          }));
                        }}
                        disabled={!isTurkiye}
                        className="min-w-0 w-full outline-none text-gray-800 text-[14px] md:text-[16px] bg-transparent appearance-none cursor-pointer disabled:cursor-not-allowed disabled:text-gray-400"
                      >
                        <option value=""></option>
                        {isTurkiye &&
                          TURKEY_CITIES.map((il) => (
                            <option key={il} value={il}>
                              {il}
                            </option>
                          ))}
                      </select>
                    </div>
                    <ChevronDown className="text-gray-400 w-4 h-4 shrink-0 pointer-events-none" />
                  </div>
                </Field>
                <Field label="İlçe">
                  <div className="relative flex items-center bg-white rounded-xl shadow-lg h-14 md:h-16 px-4 md:px-5">
                    <ChevronDown className="text-gray-400 w-5 h-5 mr-3 shrink-0" />
                    <div className="min-w-0 w-full relative">
                      {!cityDistrict && (
                        <span className="absolute inset-0 flex items-center text-gray-400 text-[12px] md:text-[14px] pointer-events-none">
                          {!isTurkiye
                            ? "Sadece Türkiye"
                            : city
                            ? ""
                            : "Önce il seçiniz..."}
                        </span>
                      )}
                      <select
                        value={form.ilce}
                        onChange={(e) => {
                          setCityDistrict(e.target.value);
                          setForm((p) => ({ ...p, ilce: e.target.value }));
                        }}
                        disabled={!city || !isTurkiye}
                        className="min-w-0 w-full outline-none text-gray-800 text-[14px] md:text-[16px] bg-transparent appearance-none cursor-pointer disabled:cursor-not-allowed disabled:text-gray-400"
                      >
                        <option value=""></option>
                        {cityDistricts.map((ilce) => (
                          <option key={ilce} value={ilce}>
                            {toTitleTR(ilce)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </Field>
              </div>
              <Field label="Açık Adres">
                <ATextarea
                  value={form.acikAdres}
                  onChange={(e) => setF({ acikAdres: e.target.value })}
                  rows={2}
                />
              </Field>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Sivas İlçe">
                  <ASelect
                    value={form.sivasIlce}
                    onChange={(e) =>
                      setF({ sivasIlce: e.target.value, sivasKoy: "" })
                    }
                  >
                    <option value="">İlçe seçiniz…</option>
                    {ilceler.map((il) => (
                      <option key={il} value={il}>
                        {il}
                      </option>
                    ))}
                  </ASelect>
                </Field>
                <Field label="Köy / Mahalle">
                  <ASelect
                    value={form.sivasKoy}
                    onChange={(e) => setF({ sivasKoy: e.target.value })}
                    disabled={!form.sivasIlce}
                  >
                    <option value="">
                      {form.sivasIlce ? "Köy seçiniz…" : "Önce ilçe seçiniz"}
                    </option>
                    {koyler.map((k) => (
                      <option key={k} value={k}>
                        {k}
                      </option>
                    ))}
                  </ASelect>
                </Field>
              </div>
              

              <SectionHeading>Sektörler</SectionHeading>
              <div className="flex gap-2">
                <AInput
                  value={form.sektorInput || ""}
                  onChange={(e) => setF({ sektorInput: e.target.value })}
                  placeholder="Sektör ekle…"
                />
                <button
                  type="button"
                  onClick={() => {
                    const v = (form.sektorInput || "").trim();
                    if (!v || (form.secilenSektorler || []).includes(v))
                      return setF({ sektorInput: "" });
                    setF({
                      secilenSektorler: [...(form.secilenSektorler || []), v],
                      sektorInput: "",
                    });
                  }}
                  className="shrink-0 px-3 py-2 rounded-lg bg-slate-900 text-white text-sm hover:bg-slate-700 transition flex items-center gap-1"
                >
                  <Plus className="w-3.5 h-3.5" /> Ekle
                </button>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {(form.secilenSektorler || []).map((s) => (
                  <span
                    key={s}
                    className="inline-flex items-center gap-1 px-2.5 py-1 bg-amber-50 text-amber-800 border border-amber-200 rounded-full text-xs font-medium"
                  >
                    {s}
                    <button
                      type="button"
                      onClick={() =>
                        setF({
                          secilenSektorler: form.secilenSektorler.filter(
                            (x) => x !== s
                          ),
                        })
                      }
                      className="text-amber-600 hover:text-red-600"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>

              <SectionHeading>Anahtar Kelimeler</SectionHeading>
              <div className="flex gap-2">
                <AInput
                  value={form.anahtarKelimeInput || ""}
                  onChange={(e) => setF({ anahtarKelimeInput: e.target.value })}
                  placeholder="Kelime ekle…"
                />
                <button
                  type="button"
                  onClick={() => {
                    const k = (form.anahtarKelimeInput || "").trim();
                    if (!k || (form.anahtarKelimeler || []).includes(k))
                      return setF({ anahtarKelimeInput: "" });
                    setF({
                      anahtarKelimeler: [...(form.anahtarKelimeler || []), k],
                      anahtarKelimeInput: "",
                    });
                  }}
                  className="shrink-0 px-3 py-2 rounded-lg bg-slate-900 text-white text-sm hover:bg-slate-700 transition flex items-center gap-1"
                >
                  <Plus className="w-3.5 h-3.5" /> Ekle
                </button>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {(form.anahtarKelimeler || []).map((k) => (
                  <span
                    key={k}
                    className="inline-flex items-center gap-1 px-2.5 py-1 bg-slate-100 text-slate-700 border border-slate-200 rounded-full text-xs"
                  >
                    {k}
                    <button
                      type="button"
                      onClick={() =>
                        setF({
                          anahtarKelimeler: form.anahtarKelimeler.filter(
                            (x) => x !== k
                          ),
                        })
                      }
                      className="text-slate-400 hover:text-red-500"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>

              <SectionHeading>Firma Hakkında</SectionHeading>
              <ATextarea
                value={form.hakkinda}
                onChange={(e) => setF({ hakkinda: e.target.value })}
                rows={6}
              />

              <SectionHeading>Ürünler & Hizmetler</SectionHeading>
              <ProductsSection
                products={editProducts}
                setProducts={setEditProducts}
              />

              {/* Butonlar */}
              <div className="flex gap-2 mb-5">
                <button
                  type="button"
                  onClick={() => setEditMode(false)}
                  className="px-4 py-2.5 rounded-xl text-sm border border-slate-200 text-slate-600 hover:bg-slate-50 transition"
                >
                  İptal
                </button>
                <button
                  type="button"
                  onClick={handleSaveOnly}
                  disabled={busy}
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-700 text-white py-2.5 rounded-xl text-sm font-semibold transition disabled:opacity-50"
                >
                  {busy ? "…" : "Kaydet"}
                </button>
                <button
                  type="button"
                  onClick={handleSaveAndApprove}
                  disabled={busy}
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white py-2.5 rounded-xl text-sm font-semibold transition disabled:opacity-50"
                >
                  <Check className="w-4 h-4" /> {busy ? "…" : "Kaydet & Onayla"}
                </button>
                <button
                  type="button"
                  onClick={handleReject}
                  disabled={busy}
                  className="px-4 py-2.5 rounded-xl text-sm bg-red-500 hover:bg-red-600 text-white font-semibold transition disabled:opacity-50"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    );

  /* ═══ LİSTE ═══ */
  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <span className="text-sm font-semibold text-slate-700">
          Onay Bekleyen Firmalar
        </span>
        {firms.length > 0 && (
          <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-xs font-bold border border-amber-200">
            {firms.length}
          </span>
        )}
      </div>

      <div className="relative mb-3">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Firma adı, şehir veya sektör ara…"
          className="w-full pl-9 pr-9 py-2.5 text-sm border border-slate-200 rounded-xl
            focus:outline-none focus:ring-2 focus:ring-slate-900 bg-slate-50 placeholder:text-slate-400"
        />
        {search && (
          <button
            onClick={() => setSearch("")}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      <p className="text-xs text-slate-400 mb-3">
        <span className="font-medium text-slate-600">{filtered.length}</span>{" "}
        firma
      </p>

      {loading && (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="h-14 bg-slate-100 rounded-xl animate-pulse"
            />
          ))}
        </div>
      )}

      {!loading && (
        <div className="space-y-2 max-h-[600px] overflow-y-auto pr-1">
          {filtered.map((firm) => (
            <button
              key={firm.id}
              type="button"
              onClick={() => openDetail(firm)}
              className="w-full flex items-center gap-3 p-3 bg-white rounded-xl border border-slate-200
                hover:border-amber-200 hover:shadow-sm transition text-left group"
            >
              <div className="w-10 h-10 rounded-lg border border-slate-100 bg-slate-50 overflow-hidden shrink-0 flex items-center justify-center">
                {firm.logoUrl ? (
                  <img
                    src={firm.logoUrl}
                    alt={firm.firmaAdi}
                    className="w-full h-full object-contain"
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                ) : (
                  <Building2 className="w-4 h-4 text-slate-300" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <span className="font-medium text-sm text-slate-900 truncate block">
                  {firm.firmaAdi || "—"}
                </span>
                <span className="text-xs text-slate-400 truncate block">
                  {[firm.sehir, firm.ilce].filter(Boolean).join(" · ")}
                  {firm.secilenSektorler?.[0] &&
                    ` · ${firm.secilenSektorler[0]}`}
                </span>
              </div>
              {firm.createdAt?.toDate && (
                <span className="text-[10px] text-slate-400 shrink-0 hidden sm:block">
                  {firm.createdAt.toDate().toLocaleDateString("tr-TR")}
                </span>
              )}
              <button
                type="button"
                onClick={(e) => handleDeleteFromList(e, firm)}
                className="shrink-0 p-1.5 rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50 transition"
                title="Sil"
              >
                <X className="w-4 h-4" />
              </button>
            </button>
          ))}

          {filtered.length === 0 && (
            <div className="text-center py-12 text-slate-400">
              <Check className="w-8 h-8 mx-auto mb-2 text-emerald-200" />
              <p className="text-sm">Onay bekleyen firma yok.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
